/**
 * TravelBudgetPanel — read-focused summary panel for a trip's budget.
 *
 * Structure:
 *   1. Total bar: spent / cap (or just "spent" when no cap is set).
 *   2. Per-category horizontal bar rows.
 *   3. Per-day burn chart — tiny inline SVG (no chart library dependency).
 *   4. Per-person owed list (split accounting).
 *
 * Quick-add and editing are handled by TravelExpenseQuickAdd (see below).
 * This panel stays read-heavy so it can live in the Trip header strip.
 */

import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, DollarSign, Sparkles, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTravelBudget, formatMoney } from '../hooks';
import { TravelExpenseQuickAdd } from './TravelExpenseQuickAdd';
import type {
  TravelExpenseCategory,
  TravelBudgetSummaryResponse,
  TravelExpenseResponse,
} from '../api';

const COMMON_CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD', 'INR', 'SGD'];

const CATEGORY_LABEL: Record<TravelExpenseCategory, string> = {
  lodging: 'Lodging',
  food: 'Food',
  transport: 'Transport',
  activity: 'Activity',
  other: 'Other',
};

const CATEGORY_COLOR: Record<TravelExpenseCategory, string> = {
  lodging: 'bg-sky-500',
  food: 'bg-emerald-500',
  transport: 'bg-indigo-500',
  activity: 'bg-amber-500',
  other: 'bg-muted-foreground',
};

/**
 * Shared dialog that lists a set of expenses with a running total and an
 * internally-scrolling body, so a long category/day never grows the modal
 * past the viewport. Used by both the "By category" and "Daily burn" popups.
 */
function ExpenseListDialog({
  open,
  onOpenChange,
  title,
  totalCents,
  homeCurrency,
  expenses,
  emptyText,
  showDate = false,
  showCategory = true,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  totalCents: number;
  homeCurrency: string;
  expenses: TravelExpenseResponse[];
  emptyText: string;
  showDate?: boolean;
  showCategory?: boolean;
}) {
  const count = expenses.length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 text-base">
            <span className="truncate">{title}</span>
            <span className="font-mono text-sm">{formatMoney(totalCents, homeCurrency)}</span>
          </DialogTitle>
          <DialogDescription>{count === 1 ? '1 expense' : `${count} expenses`}</DialogDescription>
        </DialogHeader>
        {count === 0 ? (
          <div className="text-sm text-muted-foreground italic">{emptyText}</div>
        ) : (
          <ul className="max-h-[60vh] divide-y divide-border/60 overflow-y-auto text-sm">
            {expenses.map((exp) => (
              <li key={exp.expenseId} className="flex items-start justify-between gap-2 py-2">
                <div className="min-w-0 flex-1">
                  {(showCategory || showDate) && (
                    <div className="flex items-center gap-1.5">
                      {showCategory && (
                        <span
                          className={`inline-block h-2 w-2 rounded-full ${CATEGORY_COLOR[exp.category]}`}
                        />
                      )}
                      {showCategory && (
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {CATEGORY_LABEL[exp.category]}
                        </span>
                      )}
                      {showDate && (
                        <span className="text-[10px] text-muted-foreground">{exp.incurredAt}</span>
                      )}
                    </div>
                  )}
                  <div className="truncate">{exp.description || '—'}</div>
                </div>
                <span className="whitespace-nowrap font-mono">
                  {formatMoney(exp.amountHomeCents, homeCurrency)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Per-category horizontal bars. Each row is clickable: selecting a category
 * opens a dialog listing every expense in it (filtered from the full
 * `expenses` list), with the rows showing dates since the category is fixed.
 */
function PerCategoryBars({
  summary,
  expenses,
  homeCurrency,
}: {
  summary: TravelBudgetSummaryResponse;
  expenses: TravelExpenseResponse[];
  homeCurrency: string;
}) {
  const [selectedCategory, setSelectedCategory] = useState<TravelExpenseCategory | null>(null);

  const categoryExpenses = useMemo(() => {
    if (!selectedCategory) return [];
    return expenses
      .filter((e) => e.category === selectedCategory)
      .sort((a, b) => b.amountHomeCents - a.amountHomeCents);
  }, [expenses, selectedCategory]);

  const categoryTotalCents = useMemo(
    () => categoryExpenses.reduce((sum, e) => sum + e.amountHomeCents, 0),
    [categoryExpenses],
  );

  const max = Math.max(1, ...summary.perCategory.map((c) => c.amountHomeCents));
  return (
    <div className="space-y-1.5">
      {summary.perCategory.map((c) => (
        <button
          type="button"
          key={c.category}
          className="w-full text-left text-xs group rounded hover:bg-muted/50 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onClick={() => setSelectedCategory(c.category)}
          aria-label={`${CATEGORY_LABEL[c.category]}: ${formatMoney(c.amountHomeCents, summary.homeCurrency)}, ${c.count} ${c.count === 1 ? 'entry' : 'entries'}. View expenses.`}
        >
          <div className="flex items-center justify-between">
            <span className="group-hover:underline">{CATEGORY_LABEL[c.category]}</span>
            <span className="font-mono">
              {formatMoney(c.amountHomeCents, summary.homeCurrency)}
              <span className="text-muted-foreground"> · {c.count}</span>
            </span>
          </div>
          <div className="h-1.5 rounded bg-muted overflow-hidden">
            <div
              className={`h-full ${CATEGORY_COLOR[c.category]}`}
              style={{ width: `${(c.amountHomeCents / max) * 100}%` }}
            />
          </div>
        </button>
      ))}
      {summary.perCategory.length === 0 && (
        <div className="text-xs text-muted-foreground italic">No expenses yet.</div>
      )}

      <ExpenseListDialog
        open={selectedCategory != null}
        onOpenChange={(o) => !o && setSelectedCategory(null)}
        title={selectedCategory ? CATEGORY_LABEL[selectedCategory] : ''}
        totalCents={categoryTotalCents}
        homeCurrency={homeCurrency}
        expenses={categoryExpenses}
        emptyText="No expenses in this category."
        showCategory={false}
        showDate
      />
    </div>
  );
}

/**
 * Small inline SVG bar chart for per-day spending. Height scales to the
 * single highest-spend day; bar width is `100 / days` so a short trip
 * spreads out and a long trip still fits. We keep this deliberately
 * minimal rather than pulling in a chart library.
 *
 * Bars are clickable: selecting a day opens a dialog listing that day's
 * individual expenses (filtered from the full `expenses` list by date).
 */
function BurnChart({
  summary,
  expenses,
  homeCurrency,
}: {
  summary: TravelBudgetSummaryResponse;
  expenses: TravelExpenseResponse[];
  homeCurrency: string;
}) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const days = useMemo(() => {
    // perDay comes newest-first; flip for a left-to-right timeline.
    return [...summary.perDay].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  }, [summary.perDay]);

  // Expenses for the day the user clicked, highest-cost first.
  const dayExpenses = useMemo(() => {
    if (!selectedDate) return [];
    return expenses
      .filter((e) => e.incurredAt === selectedDate)
      .sort((a, b) => b.amountHomeCents - a.amountHomeCents);
  }, [expenses, selectedDate]);

  const dayTotalCents = useMemo(
    () => dayExpenses.reduce((sum, e) => sum + e.amountHomeCents, 0),
    [dayExpenses],
  );

  if (days.length === 0) {
    return <div className="text-xs text-muted-foreground italic">No expenses yet.</div>;
  }

  const max = Math.max(1, ...days.map((d) => d.amountHomeCents));
  const barGap = 2;
  const barWidth = Math.max(4, (100 - barGap * (days.length - 1)) / days.length);

  return (
    <div className="space-y-1">
      <svg viewBox="0 0 100 32" className="w-full h-20" role="img" aria-label="Per-day burn chart">
        {days.map((d, i) => {
          const height = (d.amountHomeCents / max) * 28;
          const x = i * (barWidth + barGap);
          const y = 30 - height;
          const label = `${d.date}: ${formatMoney(d.amountHomeCents, summary.homeCurrency)}. View expenses.`;
          return (
            <g
              key={d.date}
              role="button"
              tabIndex={0}
              aria-label={label}
              className="group cursor-pointer focus:outline-none"
              onClick={() => setSelectedDate(d.date)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedDate(d.date);
                }
              }}
            >
              {/* Full-height transparent hit area so short bars are still easy to click. */}
              <rect x={x} y={0} width={barWidth} height={30} className="fill-transparent" />
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={height}
                className="fill-foreground/70 transition-colors group-hover:fill-foreground hover:fill-foreground"
              >
                <title>{label}</title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{days[0].date}</span>
        <span>{days[days.length - 1].date}</span>
      </div>

      <ExpenseListDialog
        open={selectedDate != null}
        onOpenChange={(o) => !o && setSelectedDate(null)}
        title={selectedDate ?? ''}
        totalCents={dayTotalCents}
        homeCurrency={homeCurrency}
        expenses={dayExpenses}
        emptyText="No itemized expenses for this day."
        showCategory
      />
    </div>
  );
}

function PerPersonList({ summary }: { summary: TravelBudgetSummaryResponse }) {
  if (summary.perPerson.length === 0) {
    return (
      <div className="text-xs text-muted-foreground italic">
        Split expenses between people to see who owes what. Add participants when creating an
        expense.
      </div>
    );
  }
  return (
    <div className="space-y-1 text-xs">
      {summary.perPerson.map((p) => (
        <div key={p.name} className="flex justify-between">
          <span>{p.name}</span>
          <span className={`font-mono ${p.amountHomeCents < 0 ? 'text-emerald-600' : ''}`}>
            {p.amountHomeCents >= 0
              ? `owes ${formatMoney(p.amountHomeCents, summary.homeCurrency)}`
              : `is owed ${formatMoney(-p.amountHomeCents, summary.homeCurrency)}`}
          </span>
        </div>
      ))}
    </div>
  );
}

const EXPENSE_SORT_OPTIONS = [
  { value: 'date-desc', label: 'Date (newest first)' },
  { value: 'date-asc', label: 'Date (oldest first)' },
  { value: 'category', label: 'Category' },
  { value: 'price-desc', label: 'Price (high to low)' },
  { value: 'price-asc', label: 'Price (low to high)' },
] as const;

type ExpenseSort = (typeof EXPENSE_SORT_OPTIONS)[number]['value'];

function AllExpensesSection({
  expenses,
  homeCurrency,
}: {
  expenses: TravelExpenseResponse[];
  homeCurrency: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [sortBy, setSortBy] = useState<ExpenseSort>('date-desc');

  const sortedExpenses = useMemo(() => {
    const copy = [...expenses];
    switch (sortBy) {
      case 'date-desc':
        return copy.sort((a, b) => (a.incurredAt < b.incurredAt ? 1 : -1));
      case 'date-asc':
        return copy.sort((a, b) => (a.incurredAt < b.incurredAt ? -1 : 1));
      case 'category':
        return copy.sort((a, b) => a.category.localeCompare(b.category));
      case 'price-desc':
        return copy.sort((a, b) => b.amountHomeCents - a.amountHomeCents);
      case 'price-asc':
        return copy.sort((a, b) => a.amountHomeCents - b.amountHomeCents);
    }
  }, [expenses, sortBy]);

  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span>All expenses ({expenses.length})</span>
      </button>
      {expanded && (
        <div className="mt-2 space-y-2">
          {expenses.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No expenses recorded yet.</div>
          ) : (
            <>
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as ExpenseSort)}>
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_SORT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      Sort: {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <ul className="divide-y divide-border/60 text-xs">
                {sortedExpenses.map((exp) => (
                  <li key={exp.expenseId} className="flex items-start justify-between gap-2 py-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`inline-block h-2 w-2 rounded-full ${CATEGORY_COLOR[exp.category]}`}
                        />
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {CATEGORY_LABEL[exp.category]}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          · {exp.incurredAt}
                        </span>
                      </div>
                      <div className="truncate">{exp.description || '—'}</div>
                    </div>
                    <span className="whitespace-nowrap font-mono">
                      {formatMoney(exp.amountHomeCents, homeCurrency)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Drawer-wrapped budget panel. Mirrors the TravelDossierPanel pattern: the
 * parent owns `open` state, this component handles everything inside.
 */
export function TravelBudgetPanel({
  tripId,
  open,
  onOpenChange,
  defaultItemId,
  defaultEventId,
  defaultIncurredAt,
}: {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultItemId?: string | null;
  defaultEventId?: string | null;
  defaultIncurredAt?: string;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" /> Budget &amp; expenses
          </SheetTitle>
          <SheetDescription>
            Track spending across currencies. Rates are fetched per expense date and cached so
            totals stay consistent.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <TravelBudgetPanelBody
            tripId={tripId}
            defaultItemId={defaultItemId}
            defaultEventId={defaultEventId}
            defaultIncurredAt={defaultIncurredAt}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** Inline version — used inside the drawer above, but also usable on its
 * own inside a page if a future layout wants the panel always visible. */
export function TravelBudgetPanelBody({
  tripId,
  defaultItemId,
  defaultEventId,
  defaultIncurredAt,
}: {
  tripId: string;
  defaultItemId?: string | null;
  defaultEventId?: string | null;
  defaultIncurredAt?: string;
}) {
  const {
    budget,
    expenses,
    summary,
    loading,
    createExpense,
    isCreatingExpense,
    updateBudget,
    isUpdatingBudget,
    estimateBudget,
    isEstimatingBudget,
    featureEnabled,
  } = useTravelBudget(tripId);

  const [totalDraft, setTotalDraft] = useState('');
  const [currencyDraft, setCurrencyDraft] = useState('');
  const [confirmRefreshOpen, setConfirmRefreshOpen] = useState(false);

  const runEstimate = async () => {
    try {
      const res = await estimateBudget();
      toast({
        title: res.estimatedCount + res.flightCount > 0 ? 'Budget estimated' : 'No estimates added',
        description: res.message,
      });
    } catch (err) {
      toast({
        title: 'Could not generate budget',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  // Keep the input in sync when the budget row loads the first time.
  useMemo(() => {
    if (budget?.totalCents != null) {
      setTotalDraft((budget.totalCents / 100).toFixed(2));
    } else if (budget !== null) {
      setTotalDraft('');
    }
    if (budget?.homeCurrency) setCurrencyDraft(budget.homeCurrency);
  }, [budget]);

  if (!featureEnabled) {
    return (
      <div className="text-sm text-muted-foreground">
        Budget tracking is disabled. Re-enable it from Settings → Budget.
      </div>
    );
  }
  if (loading && !summary) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Budget</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!summary) return null;

  const home = summary.homeCurrency;
  const hasExpenses = summary.expenseCount > 0;
  const spentPct =
    summary.totalCents && summary.totalCents > 0
      ? Math.min(100, (summary.spentHomeCents / summary.totalCents) * 100)
      : null;

  const commitBudget = () => {
    const raw = totalDraft.trim();
    const totalCents = raw === '' ? null : Math.round(parseFloat(raw) * 100);
    const nextCurrency = currencyDraft.trim().toUpperCase();
    void updateBudget({
      homeCurrency: nextCurrency || undefined,
      totalCents: Number.isFinite(totalCents) ? totalCents : null,
    });
  };

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-sm">Budget</CardTitle>
        <div className="flex items-center gap-2">
          {hasExpenses ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmRefreshOpen(true)}
              disabled={isEstimatingBudget}
            >
              {isEstimatingBudget ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1.5" />
              )}
              Refresh budget
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void runEstimate()}
              disabled={isEstimatingBudget}
            >
              {isEstimatingBudget ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-1.5" />
              )}
              Autogenerate budget
            </Button>
          )}
          <TravelExpenseQuickAdd
            trigger={
              <Button size="sm" variant="secondary">
                + Add expense
              </Button>
            }
            defaultItemId={defaultItemId ?? null}
            defaultEventId={defaultEventId ?? null}
            defaultIncurredAt={defaultIncurredAt}
            defaultCurrency={home}
            onSubmit={(input) => createExpense(input)}
            isSubmitting={isCreatingExpense}
          />
        </div>
      </CardHeader>

      <AlertDialog open={confirmRefreshOpen} onOpenChange={setConfirmRefreshOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Refresh budget with new estimates?</AlertDialogTitle>
            <AlertDialogDescription>
              This replaces the AI-generated estimates currently in your budget (lodging, food,
              transport, activities, and flight fares) with fresh ones. Expenses you added or edited
              by hand are kept. This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void runEstimate()}>Refresh budget</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Spent</span>
            <span className="font-mono">
              {formatMoney(summary.spentHomeCents, home)}
              {summary.totalCents != null && (
                <>
                  {' '}
                  /{' '}
                  <span className="text-muted-foreground">
                    {formatMoney(summary.totalCents, home)}
                  </span>
                </>
              )}
            </span>
          </div>
          {spentPct != null && (
            <div className="h-2 rounded bg-muted overflow-hidden">
              <div
                className={`h-full ${spentPct >= 100 ? 'bg-destructive' : 'bg-foreground'}`}
                style={{ width: `${spentPct}%` }}
              />
            </div>
          )}
          {summary.remainingHomeCents != null && (
            <div className="text-xs text-muted-foreground">
              {summary.remainingHomeCents >= 0
                ? `${formatMoney(summary.remainingHomeCents, home)} remaining`
                : `${formatMoney(-summary.remainingHomeCents, home)} over budget`}
            </div>
          )}
        </div>

        {/* Budget inputs — edit home currency + cap inline */}
        <div className="border-t border-border pt-3 grid grid-cols-2 gap-2 text-xs">
          <div>
            <Label className="text-[10px]">Home currency</Label>
            <Select value={currencyDraft} onValueChange={setCurrencyDraft}>
              <SelectTrigger>
                <SelectValue placeholder="USD" />
              </SelectTrigger>
              <SelectContent>
                {COMMON_CURRENCIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px]">Total cap</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={totalDraft}
              onChange={(e) => setTotalDraft(e.target.value)}
              onBlur={commitBudget}
              placeholder="no cap"
            />
          </div>
          {isUpdatingBudget && (
            <div className="col-span-2 text-[10px] text-muted-foreground">Saving…</div>
          )}
        </div>

        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
            By category
          </div>
          <PerCategoryBars summary={summary} expenses={expenses} homeCurrency={home} />
        </div>

        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
            Daily burn
          </div>
          <BurnChart summary={summary} expenses={expenses} homeCurrency={home} />
        </div>

        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
            Per person
          </div>
          <PerPersonList summary={summary} />
        </div>

        <AllExpensesSection expenses={expenses} homeCurrency={home} />
      </CardContent>
    </Card>
  );
}
